import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Circle, CheckCircle2 } from "lucide-react";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const highlightedOrder = params.get("order");

  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  useEffect(() => { if (user) api.get("/orders").then((r) => setOrders(r.data)); }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-950">Hi, {user.name}</h1>
          <p className="text-slate-600 mt-1">{user.email}</p>
        </div>
        <button onClick={logout} className="border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-xl text-sm" data-testid="account-logout">Sign out</button>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-6">
          <Package className="w-5 h-5 text-blue-900" />
          <h2 className="font-heading text-xl font-semibold">Your Orders</h2>
        </div>
        {orders.length === 0 ? (
          <div className="border border-slate-200 rounded-2xl p-14 text-center">
            <p className="text-slate-600 mb-4">No orders yet</p>
            <Link to="/products" className="bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm">Start shopping</Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((o) => (
              <div key={o.order_id} className={`bg-white border rounded-2xl p-6 ${highlightedOrder === o.order_id ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200"}`} data-testid={`order-${o.order_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Order ID</div>
                    <div className="font-mono text-sm text-slate-900">{o.order_id}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Placed</div>
                    <div className="text-sm">{new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Total</div>
                    <div className="font-heading font-bold text-slate-950">₹{o.total.toLocaleString("en-IN")}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Payment</div>
                    <div className={`text-sm font-medium ${o.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>{o.payment_status}</div>
                  </div>
                </div>

                <div className="grid gap-3 mb-4">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex items-center gap-3 text-sm">
                      <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-50" />
                      <div className="flex-1"><div className="line-clamp-1 font-medium">{it.name}</div><div className="text-xs text-slate-500">Qty {it.quantity}</div></div>
                      <div>₹{(it.price * it.quantity).toLocaleString("en-IN")}</div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Tracking</div>
                  <div className="flex items-center gap-2 overflow-x-auto">
                    {o.tracking.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 shrink-0">
                        {t.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
                        <span className={`text-xs ${t.done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{t.stage}</span>
                        {i < o.tracking.length - 1 && <div className="w-6 h-px bg-slate-200"></div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
